"use client";

import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { FieldWrapper } from "./context";
import type { FieldRenderContext } from "./context";

const PROBABILITY_STEP = 1;
const clampProbability = (n: number) => Math.min(100, Math.max(0, Math.round(n)));

/** Interpolate color: 0=red, 50=yellow, 100=green */
function getProbabilityColor(pct: number): string {
  const r = (a: number, b: number, t: number) => Math.round(a + (b - a) * t);
  if (pct <= 50) {
    const t = pct / 50;
    return `rgb(${r(239, 234, t)} ${r(68, 179, t)} ${r(68, 8, t)})`;
  }
  const t = (pct - 50) / 50;
  return `rgb(${r(234, 34, t)} ${r(179, 197, t)} ${r(8, 94, t)})`;
}

/**
 * Render a probability slider (same UX as deal table inline edit).
 * Label shows "Probability [num]%" (live updates). Slider has a clickable tooltip above it for typing.
 */
export const ProbabilitySliderFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, setValue, watchedValues, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;
  const rawValue = watchedValues[fieldName];
  const num =
    rawValue === "" || rawValue === undefined || rawValue === null
      ? 0
      : clampProbability(Number(rawValue) || 0);

  const [isTyping, setIsTyping] = useState(false);
  const [inputStr, setInputStr] = useState(String(num));

  useEffect(() => {
    if (!isTyping) setInputStr(String(num));
  }, [num, isTyping]);

  const apply = (value: number) => {
    const clamped = clampProbability(value);
    setValue(fieldName, clamped, { shouldValidate: true });
    setInputStr(String(clamped));
  };

  const handleInputBlur = () => {
    const v = inputStr === "" ? 0 : Number(inputStr);
    apply(Number.isNaN(v) ? num : v);
    setIsTyping(false);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const v = (e.target as HTMLInputElement).value === "" ? 0 : Number((e.target as HTMLInputElement).value);
      apply(Number.isNaN(v) ? num : v);
      setIsTyping(false);
      (e.target as HTMLInputElement).blur();
    }
  };

  const probabilityColor = getProbabilityColor(num);

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage}
      labelOverride={
        <span className="font-medium">
          Probability{" "}
          {isTyping ? (
            <Input
              type="number"
              min={0}
              max={100}
              value={inputStr}
              onChange={(e) => setInputStr(e.target.value)}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              placeholder="0–100"
              className={cn(
                "inline-flex w-12 h-6 text-sm tabular-nums text-center font-medium p-0 border-b-2 border-transparent focus:border-foreground rounded-none",
                hasError && "border-red-500"
              )}
              style={{ color: probabilityColor }}
              autoFocus
              aria-label="Probability percentage"
            />
          ) : (
            <button
              type="button"
              onClick={() => {
                setIsTyping(true);
                setInputStr(String(num));
              }}
              className="tabular-nums underline decoration-dotted underline-offset-2 hover:decoration-solid cursor-pointer focus:outline-none rounded px-0.5 -mx-0.5"
              style={{ color: probabilityColor }}
              title="Click to type"
            >
              {num}
            </button>
          )}
          %
        </span>
      }
    >
      <div className="flex items-center gap-1.5 min-w-[180px]">
        <button
          type="button"
          aria-label="Decrease probability"
          onClick={() => apply(num - PROBABILITY_STEP)}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-input bg-background text-muted-foreground text-xs hover:bg-muted hover:text-foreground transition-colors select-none"
        >
          −
        </button>
        <div className="relative flex-1 min-w-[100px]">
          <input
            type="range"
            min={0}
            max={100}
            value={num}
            onChange={(e) => apply(Number(e.target.value))}
            className="h-10 w-full cursor-pointer probability-slider"
            style={{ "--probability-thumb-color": probabilityColor } as React.CSSProperties}
            aria-invalid={hasError}
          />
        </div>
        <button
          type="button"
          aria-label="Increase probability"
          onClick={() => apply(num + PROBABILITY_STEP)}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-input bg-background text-muted-foreground text-xs hover:bg-muted hover:text-foreground transition-colors select-none"
        >
          +
        </button>
      </div>
    </FieldWrapper>
  );
};
